/**
 * Iter-163 — Live AI Agent activity indicator.
 *
 * Polls `/api/v1/sourcing/agents/{moduleKey}/activity` every 20s and renders:
 *   • Green dot + "Last AI {action}: 12s ago" when there's a recent event
 *   • Amber dot + "Heuristic mode" when the most recent event was deterministic
 *   • Slate dot + "Idle" when nothing recent
 *
 * Pulses for 3s after a fresh event lands.
 *
 * Mount inside SourcingAgentPanel header:
 *   <AgentActivityIndicator moduleKey="cld21" />
 */
import { useEffect, useRef, useState } from 'react';
import { Activity } from 'lucide-react';
import api from '@/lib/api';

function formatAge(ts) {
  if (!ts) return '—';
  const ms = Date.now() - new Date(ts).getTime();
  if (Number.isNaN(ms) || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

const ACTION_LABEL = {
  'claim.': 'Claim run',
  'claim.created': 'Claim created',
  'claim.initiated': 'Claim initiated',
  'claim.fm.': 'FM validation',
  'fm.': 'FM event',
  'evidence.': 'Evidence classified',
  'quantum.': 'Quantum assessed',
  'ld.clause.': 'LD clause updated',
  'ld.accrual.': 'LD accrued',
  'ld.audit.': 'LD audit',
  'ld.notice.': 'LD notice issued',
  'negotiation.': 'Round logged',
  'settlement.': 'Settlement modelled',
  'claim.settled': 'Claim settled',
  'adjudication.': 'Adjudication update',
  'adjudication.case.': 'Case room update',
  'adjudication.filing.': 'Filing added',
  'recovery.': 'Recovery aged',
  'predictive.': 'Risk forecast',
  'risk.score.': 'Risk score',
  'contract.': 'Contract run',
};

function summariseEvent(eventType) {
  if (!eventType) return 'AI run';
  // Find best-matching prefix
  const keys = Object.keys(ACTION_LABEL).sort((a, b) => b.length - a.length);
  for (const k of keys) {
    if (eventType.startsWith(k)) return ACTION_LABEL[k];
  }
  return eventType.split('.').slice(0, 2).join(' ');
}

export function AgentActivityIndicator({ moduleKey }) {
  const [activity, setActivity] = useState(null);
  const [pulse, setPulse] = useState(false);
  const lastTsRef = useRef(null);

  useEffect(() => {
    if (!moduleKey) return;
    let timer;
    const fetchActivity = async () => {
      try {
        const r = await api.get(`/v1/sourcing/agents/${moduleKey}/activity`);
        const a = r.data?.lastActivity || null;
        setActivity(a);
        if (a?.ts && a.ts !== lastTsRef.current) {
          // New event since last poll — pulse
          if (lastTsRef.current) {
            setPulse(true);
            setTimeout(() => setPulse(false), 3000);
          }
          lastTsRef.current = a.ts;
        }
      } catch (e) {
        // Silent — keep previous state
      }
    };
    fetchActivity();
    timer = setInterval(fetchActivity, 20_000);
    return () => clearInterval(timer);
  }, [moduleKey]);

  // Force re-render every 10s so the relative time stays fresh
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 10_000);
    return () => clearInterval(t);
  }, []);

  if (!activity) {
    // iter-227 — replaced "Idle" placeholder with a specialist-ready
    // signal so agent panels don't look dead when the user first
    // opens the page.
    return (
      <div className="flex items-center gap-1.5 text-[10px] text-emerald-700"
            data-testid="agent-activity-idle">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        <span>Ready</span>
      </div>
    );
  }

  const isAi = activity.aiSource === 'AI';
  const dotColor = isAi ? 'bg-emerald-500' : 'bg-amber-500';
  const action = summariseEvent(activity.eventType);
  const age = formatAge(activity.ts);

  return (
    <div className="flex items-center gap-1.5 text-[10px]"
          data-testid="agent-activity-indicator">
      <span className={`w-1.5 h-1.5 rounded-full ${dotColor} ${pulse ? 'animate-ping' : ''}`}
            data-testid="agent-activity-dot" />
      <Activity className="w-2.5 h-2.5 text-slate-500" />
      <span className="text-slate-600 font-medium">{action}</span>
      <span className="text-slate-400">·</span>
      <span className="text-slate-500">{age}</span>
    </div>
  );
}

export default AgentActivityIndicator;
